/**
 * The filter bar every report renders — CLAUDE.md §4.2, §4.4. It takes the report's filter schema
 * and nothing else: applied values live in the URL, so a copied link reproduces what the sender saw.
 *
 * **Edits are a draft until Query is pressed** — every URL write is a fresh ADB query, so staging
 * them means one press answers one question. The URL stays the single source of truth for what is
 * *applied*; the draft is uncommitted form input.
 */

import { Fragment, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faTriangleExclamation, faXmark } from '@fortawesome/free-solid-svg-icons';

import { Button } from '@/components/ui/button';
import { ButtonGroupSelector } from '@/components/report/ButtonGroupSelector';
import {
  DateFilter,
  DateRangeFilter,
  MultiSelectFilter,
  SelectFilter,
  SwitchFilter,
  TextFilter,
} from '@/components/report/filter-controls';
import { useReportFilters } from '@/components/report/useReportFilters';
import {
  ANY_VALUE,
  decodeMultiValue,
  isLive,
  optionsOf,
  type FilterSchema,
  type FilterValues,
} from '@/lib/filters';
import { cn } from '@/lib/utils';
import { useI18n } from '@/lib/i18n';

export interface FilterBarProps {
  /** The report's filters, declared as data in `reports/<id>/filters.ts`. */
  schema: FilterSchema;
  /**
   * Extra controls after the declared filters. **Pass a fragment, not a wrapper `<div>`** — these
   * are direct children of the filter row, and a wrapper is one flex item that takes its own line.
   */
  afterFilters?: React.ReactNode;
  /** Insert `afterFilters` immediately before this declared field instead of after every field. */
  afterFiltersBefore?: string;
  /** Rendered at the end of the bar — the export control (§5.9). */
  actions?: React.ReactNode;
  className?: string;
}

export function FilterBar({
  schema,
  afterFilters,
  afterFiltersBefore,
  actions,
  className,
}: FilterBarProps) {
  const { text } = useI18n();
  const {
    values,
    issues,
    isActive,
    setValues,
    clear,
    // The schema with live option lists filled in; the declared one draws empty drop-downs forever.
    schema: resolved,
    pendingOptions,
  } = useReportFilters(schema);

  // Staged edits, keyed like the URL params they will become.
  const [pending, setPending] = useState<Partial<FilterValues>>({});

  /**
   * Whether a staged value has been overtaken by its options arriving. A draft survives a schema
   * rebuild and `applyFilterValues` checks no values, so without this a value staged against a
   * loading list could be committed after the list came back and disagreed with it.
   */
  const stagedIsStale = (param: string, value: string | null): boolean => {
    if (value === null || value === ANY_VALUE) {
      return false;
    }
    const field = resolved.find((candidate) => candidate.name === param);
    if (field === undefined || (field.type !== 'select' && field.type !== 'multi-select')) {
      return false;
    }
    if (isLive(field.options) || pendingOptions.has(param)) {
      return false;
    }
    const allowed = new Set(optionsOf(field.options).map((option) => option.value));
    return field.type === 'select'
      ? !allowed.has(value)
      : !decodeMultiValue(value).every((token) => allowed.has(token));
  };

  const staged: FilterValues = {};
  for (const [param, value] of Object.entries(pending)) {
    if (value !== undefined && !stagedIsStale(param, value)) {
      staged[param] = value;
    }
  }

  const draft: FilterValues = { ...values, ...staged };

  const isDirty = Object.entries(staged).some(
    ([param, value]) => (value ?? null) !== (values[param] ?? null),
  );

  const stage = (changes: Partial<FilterValues>) => {
    setPending((current) => ({ ...current, ...changes }));
  };

  const apply = () => {
    if (isDirty) {
      setValues(staged);
    }
    setPending({});
  };

  const clearAll = () => {
    setPending({});
    clear();
  };

  const insertBeforeDeclaredField =
    afterFiltersBefore !== undefined
    && resolved.some((field) => field.name === afterFiltersBefore);

  const renderField = (field: FilterSchema[number]) => {
    switch (field.type) {
      case 'date-range':
        return (
          <DateRangeFilter
            field={field}
            values={draft}
            onChange={stage}
          />
        );
      case 'date':
        return (
          <DateFilter
            field={field}
            value={draft[field.name] ?? null}
            onChange={(next) => stage({ [field.name]: next })}
          />
        );
      case 'select':
        if (field.presentation === 'segmented') {
          return (
            <ButtonGroupSelector
              label={field.label}
              options={optionsOf(field.options)}
              value={draft[field.name] ?? ''}
              onChange={(next) => stage({ [field.name]: next })}
            />
          );
        }
        return (
          <SelectFilter
            field={field}
            value={draft[field.name] ?? null}
            pending={pendingOptions.has(field.name)}
            onChange={(next) => stage({ [field.name]: next })}
          />
        );
      case 'multi-select':
        return (
          <MultiSelectFilter
            field={field}
            value={draft[field.name] ?? null}
            pending={pendingOptions.has(field.name)}
            onChange={(next) => stage({ [field.name]: next })}
          />
        );
      case 'switch':
        return (
          <SwitchFilter
            field={field}
            value={draft[field.name] ?? null}
            onChange={(next) => stage({ [field.name]: next })}
          />
        );
      case 'text':
        return (
          <TextFilter
            field={field}
            value={draft[field.name] ?? null}
            onChange={(next) => stage({ [field.name]: next })}
          />
        );
    }
  };

  return (
    // `p-1.5` is the floor: less clips the focus ring on the outermost control, which reads as a
    // rendering fault (§4.9).
    <section
      aria-label={text('Filters')}
      className={cn('flex flex-col gap-1.5 rounded-lg border bg-card p-1.5', className)}
    >
      <div className="flex flex-wrap items-end gap-x-1.5 gap-y-1.5">
        {resolved.map((field) => (
          <Fragment key={field.name}>
            {field.name === afterFiltersBefore ? afterFilters : null}
            {renderField(field)}
          </Fragment>
        ))}

        {insertBeforeDeclaredField ? null : afterFilters}

        <div className="ml-auto flex items-end gap-1.5 self-end">
          {isActive ? (
            <Button variant="ghost" size="sm" onClick={clearAll} className="h-8 gap-1.5">
              <FontAwesomeIcon icon={faXmark} className="size-3.5" />
              {text('Clear filters')}
            </Button>
          ) : null}
          {/* Inert while nothing changed — the report on screen already answers the applied filters. */}
          <Button size="sm" onClick={apply} disabled={!isDirty} className="h-8 gap-1.5">
            <FontAwesomeIcon icon={faPlay} className="size-3" />
            {text('Query')}
          </Button>
          {actions}
        </div>
      </div>

      {issues.length > 0 ? (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 px-3 py-2 text-sm text-destructive">
          <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 size-4 shrink-0" />
          <ul className="flex flex-col gap-1">
            {issues.map((issue) => (
              <li key={issue.param}>
                <span className="font-medium">{issue.label}:</span> {issue.message}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
